import { Alert, Button, TextField } from "@mui/material";
import React, { useState } from "react";
import customHttp from "../../services/customHTTP";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

const PasswordForm = ({ currentUser }) => {
	const [currentPassword, setCurrentPassword] = useState("");
	const [newPassword, setNewPassword] = useState("");
	const [confirmPassword, setConfirmPassword] = useState("");
	const [passwordsMatchError, setPasswordsMatchError] = useState(false);
	const [successMessage, setSuccessMessage] = useState("");
	const [errorMessage, setErrorMessage] = useState("");
	const [validation, setValidation] = useState({
		minLength: false,
		uppercase: false,
		specialChar: false,
		number: false,
		confirmPass: false,
	});

	const validatePassword = (password) => {
		const minLength = password.length >= 8;
		const uppercase = /[A-Z]/.test(password);
		const specialChar = /[!@#$%^&*]/.test(password);
		const number = /[0-9]/.test(password);

		setValidation({
			minLength,
			uppercase,
			specialChar,
			number,
		});
	};

	const validateConfirmPassword = (confirmPasswd) => {
		const confirmpass = newPassword === confirmPasswd;
		setValidation({
			...validation,
			confirmpass,
		});
	};

	const isFormValid = () => {
		return (
			validation.minLength &&
			validation.uppercase &&
			validation.specialChar &&
			validation.number &&
			newPassword === confirmPassword
		);
	};

	const handlePasswordSubmit = async (e) => {
		e.preventDefault();

		// Check if new password and confirm password match
		if (newPassword !== confirmPassword) {
			setPasswordsMatchError(true);
			return;
		} else {
			setPasswordsMatchError(false);
		}

		const reqBody = {
			userId: currentUser.user_id, // Set userId dynamically based on logged-in user
			current_pass: currentPassword,
			pass: newPassword,
			c_pass: confirmPassword,
		};
		if (!reqBody.current_pass || !reqBody.pass || !reqBody.c_pass) {
			setErrorMessage("All fields are required.");
			return;
		}
		try {
			// Make the API call
			const response = await customHttp.post("users/updatepassword", reqBody);

			// Check if the HTTP response status is successful (2xx)
			if (response.status >= 200 && response.status < 300) {
				// Handle the response based on the status field
				if (response.data.status === "success") {
					setConfirmPassword("");
					setCurrentPassword("");
					setNewPassword("");
					setSuccessMessage(response.data.message);

					// Remove success message after 3 seconds
					setTimeout(() => {
						setSuccessMessage("");
						setValidation({
							minLength: false,
							uppercase: false,
							specialChar: false,
							number: false,
							confirmPass: false,
						});
					}, 3000);
				} else if (response.data.status === "error") {
					setErrorMessage(response.data.message);
					setTimeout(() => {
						setErrorMessage("");
					}, 6000);
				} else {
					// Handle failure (show an error alert if needed)
					console.error("Error:", response.data.message);
				}
			} else {
				// If the response status is not 2xx, throw an error
				throw new Error(`Unexpected HTTP status code: ${response.status}`);
			}
		} catch (error) {
			// Log the error and handle it in the frontend
			console.error("Error updating password:", error);
			setErrorMessage("An unexpected error occurred. Please try again.");
			setTimeout(() => {
				setErrorMessage("");
			}, 6000);
		}
	};
	return (
		<>
			{successMessage && (
				<Alert severity="success" sx={{ mt: 2, mb: 2 }}>
					{successMessage}
				</Alert>
			)}
			{errorMessage && (
				<Alert severity="error" sx={{ mt: 2, mb: 2 }}>
					{errorMessage}
				</Alert>
			)}
			<div className="form-section">
				<TextField
					label="Current Password"
					type="password"
					fullWidth
					size="small"
					className="form-input"
					value={currentPassword}
					onChange={(e) => setCurrentPassword(e.target.value)}
				/>
				<TextField
					label="New Password"
					type="password"
					fullWidth
					size="small"
					className="form-input"
					value={newPassword}
					onChange={(e) => {
						const newPassword = e.target.value;
						setNewPassword(newPassword);
						validatePassword(newPassword);
					}}
					error={passwordsMatchError}
					helperText={passwordsMatchError && "Passwords do not match"}
				/>
				<TextField
					label="Confirm New Password"
					type="password"
					fullWidth
					size="small"
					className="form-input"
					value={confirmPassword}
					onChange={(e) => {
						setConfirmPassword(e.target.value);
						validateConfirmPassword(e.target.value);
					}}
					error={passwordsMatchError}
					helperText={passwordsMatchError && "Passwords do not match"}
				/>
				<div className="password-validation" style={{ marginBottom: "15px" }}>
					<p
						style={{
							fontSize: "11px",
							color: validation.minLength ? "green" : "#aaa",
							marginBottom: 0,
						}}>
						{validation.minLength ? (
							<CheckCircleIcon fontSize="11px" />
						) : (
							<ErrorOutlineIcon fontSize="11px" />
						)}{" "}
						Minimum 8 characters
					</p>
					<p
						style={{
							fontSize: "11px",
							color: validation.uppercase ? "green" : "#aaa",
							marginBottom: 0,
						}}>
						{validation.uppercase ? (
							<CheckCircleIcon fontSize="11px" />
						) : (
							<ErrorOutlineIcon fontSize="11px" />
						)}{" "}
						At least one uppercase letter
					</p>
					<p
						style={{
							fontSize: "11px",
							color: validation.specialChar ? "green" : "#aaa",
							marginBottom: 0,
						}}>
						{validation.specialChar ? (
							<CheckCircleIcon fontSize="11px" />
						) : (
							<ErrorOutlineIcon fontSize="11px" />
						)}{" "}
						At least one special character
					</p>
					<p
						style={{
							fontSize: "11px",
							color: validation.number ? "green" : "#aaa",
							marginBottom: 0,
						}}>
						{validation.number ? <CheckCircleIcon fontSize="11px" /> : <ErrorOutlineIcon fontSize="11px" />}{" "}
						At least one number
					</p>
					<p
						style={{
							fontSize: "11px",
							color: validation.confirmpass ? "green" : "#aaa",
							marginBottom: 0,
						}}>
						{validation.confirmpass ? (
							<CheckCircleIcon fontSize="11px" />
						) : (
							<ErrorOutlineIcon fontSize="11px" />
						)}{" "}
						Confirm Password Matches
					</p>
				</div>
				<Button
					disabled={!isFormValid()}
					variant="contained"
					color="primary"
					className="button-primary"
					onClick={handlePasswordSubmit}>
					Update Password
				</Button>
			</div>
		</>
	);
};

export default PasswordForm;
